import { APP_BASE_HREF, isPlatformBrowser } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpRequest,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_ID, importProvidersFrom, inject, PLATFORM_ID, provideAppInitializer,provideZoneChangeDetection } from '@angular/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { RouteReuseStrategy } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import { environment } from '@env/environment';
import { provideEffects } from '@ngrx/effects';
import { TranslateModule } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';

import { LOCATION } from '@personal-area/common';
import { License, provideCore } from '@personal-area/core';
import { ACCOUNT_VIEWER_CONFIG } from '@personal-area/global/account-viewer';
import {
  AccountEffects,
  accountsReducer,
  tariffReducer,
  TariffsEffects,
} from '@personal-area/global/accounts/services';
import { AuthInterceptor } from '@personal-area/global/authorization/services';
import { BILLING_CONFIG } from '@personal-area/global/billing/feature';
import {
  DEVICE_DETECTOR,
  DeviceDetectorProvider,
} from '@personal-area/global/browser-detection';
import {
  AllCountriesEffects,
  allCountriesReducer,
  CountriesEffects,
  countriesReducer,
} from '@personal-area/global/country';
import { DOCUMENT_LINKS_CONFIG } from '@personal-area/global/document-links';
import { HeadersValidatorInterceptor } from '@personal-area/global/http/services';
import {
  I18N_CONFIG,
  I18nBrowserModule,
  I18nService,
} from '@personal-area/global/i18n';
import { providePartner } from '@personal-area/global/ib/models';
import { provideLiveChat } from '@personal-area/global/livechat';
import { loginAnalyticsReducer } from '@personal-area/global/login-analytics';
import {
  NAVIGATION_LAYOUT_CONFIG,
  NavigationLayoutConfig,
} from '@personal-area/global/navigation-layout';
import { IS_JS_ENABLED } from '@personal-area/global/password';
import { providePlatformLinks } from '@personal-area/global/platform-links';
import {
  REWARDS_HUB_CONFIG,
  type RewardsHubConfig,
} from '@personal-area/global/rewards-hub/feature-rewards-hub';
import { SOCIAL_AUTH_CONFIG } from '@personal-area/global/social-authorization/models';
import { provideSocialNetworks } from '@personal-area/global/social-networks';
import { UNAVAILABLE_MODAL_CONFIG } from '@personal-area/global/unavailable-method-modal';
import { UserEffects, userReducer } from '@personal-area/global/users/services';
import {
  VERIFICATION_CONFIG,
  verificationProviders,
} from '@personal-area/global/verification';
import { WebsocketFallbackService } from '@personal-area/global/websocket';
import { LAST_LOGIN_CONFIG } from '@personal-area/shared/authorization';
import { provideStorage } from '@personal-area/storage';

import { appRouting } from './app-routing.module';
import {
  defaultDialogMaxHeight,
  defaultDialogMaxWidth,
  defaultDialogMinWidth,
  defaultDialogWidth,
  isServiceWorkerEnabled,
  serviceWorkerSetupDelay,
} from './constants/app.constants';
import { RequestInterceptor } from './core/interceptors/request.interceptor';
import { RouteReusableStrategy } from './core/routing/route-reusable-strategy';
import { GlobalWebsocketFallbackService } from './core/services/user/global.websocket-fallback.service';

export const appProviders = [
  provideZoneChangeDetection(),
  provideHttpClient(withInterceptorsFromDi(), withFetch()),
  provideAnimationsAsync(),
  provideClientHydration(
    withEventReplay(),
    withHttpTransferCacheOptions({
      includePostRequests: false,
      filter: (req: HttpRequest<unknown>) => !req.url.includes('/assets/i18n/'),
    }),
  ),
  provideCore(
    {
      syncLanguage: true,
      isServiceWorkerEnabled,
      isStage: environment.isStage,
      isProd: environment.production,
      isWebview: false,
      baseHref: '/cabinet',
      redirectURLAfterLogin: environment.redirectURLAfterLogin,
      defaultLanguage: environment.defaultLanguage,
      defaultLocation: environment.defaultLocation,
      supportedLanguages: environment.supportedLanguages,
      domainLanguages: environment.domainLanguages,
      restrictedCountries: environment.restrictedCountries,
      logger: {
        logLevel: environment.production ? 0 : 3,
      },
      serverUrl: environment.serverUrl,
      store: {
        keysForSessionStorage: [],
        keysForLocalStorage: ['user.options'],
      },
      license: License.Global,
      helpCenterDomain: 'fbs.helpcenter.io',
    },
    {
      countries: countriesReducer,
      allCountries: allCountriesReducer,
      accounts: accountsReducer,
      tariffs: tariffReducer,
      user: userReducer,
      'login-analytics': loginAnalyticsReducer,
    },
  ),
  provideEffects([
    CountriesEffects,
    AllCountriesEffects,
    AccountEffects,
    TariffsEffects,
    UserEffects,
  ]),
  {
    multi: true,
    provide: HTTP_INTERCEPTORS,
    useClass: HeadersValidatorInterceptor,
  },
  {
    multi: true,
    provide: HTTP_INTERCEPTORS,
    useClass: RequestInterceptor,
  },
  {
    multi: true,
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
  },
  {
    provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
    useValue: {
      appearance: 'outline',
      subscriptSizing: 'dynamic',
    },
  },
  {
    provide: MAT_DIALOG_DEFAULT_OPTIONS,
    useValue: {
      closeOnNavigation: true,
      maxWidth: defaultDialogMaxWidth,
      minWidth: defaultDialogMinWidth,
      width: defaultDialogWidth,
      maxHeight: defaultDialogMaxHeight,
      panelClass: ['common-popup-container'],
    },
  },
  {
    provide: DEVICE_DETECTOR,
    useFactory: DeviceDetectorProvider,
  },
  { provide: APP_ID, useValue: environment.appId },
  { provide: APP_BASE_HREF, useValue: '/cabinet' },
  provideAppInitializer(() => {
    inject(CookieService);
    return I18nService.initializeI18nFactory(inject(I18nService))();
  }),
  {
    provide: WebsocketFallbackService,
    useClass: GlobalWebsocketFallbackService,
  },
  {
    provide: SOCIAL_AUTH_CONFIG,
    useValue: environment.socialAuth,
  },
  provideServiceWorker('/cabinet/ngsw-worker-polyfill.js', {
    enabled: isServiceWorkerEnabled,
    registrationStrategy: `registerWhenStable:${serviceWorkerSetupDelay}`,
  }),
  {
    provide: RouteReuseStrategy,
    useClass: RouteReusableStrategy,
  },
  provideStorage({
    isTest: environment.isStage,
    secretKey: environment.secretKey,
    version: 'v1',
  }),
  importProvidersFrom(TranslateModule, I18nBrowserModule),
  providePartner({ isStandalone: false }),
  {
    provide: VERIFICATION_CONFIG,
    useValue: {},
  },
  {
    provide: REWARDS_HUB_CONFIG,
    useValue: {
      hiddenPromoByName: [],
    } as RewardsHubConfig,
  },
  {
    provide: BILLING_CONFIG,
    useValue: {
      cryptoTextKey: 'finances.crypto-info-list',
    },
  },
  {
    provide: DOCUMENT_LINKS_CONFIG,
    useValue: {
      documentPrefixesByDomain: environment.documentPrefixesByDomain,
      rootDomain: environment.rootDomain,
      defaultLanguage: environment.defaultLanguage,
      getCustomerAgreementUrl: (docPrefix, countryPrefix, language) =>
        `/docs/${docPrefix}${countryPrefix}FBS_agreement_${language}.pdf`,
      getTradingConditionsUrl: (docPrefix) =>
        `/docs/${docPrefix}Trading_conditions_en.pdf`,
      getRiskWarningUrl: (docPrefix, language) =>
        `/docs/${docPrefix}risk_warning_${language}.pdf`,
      getAmlPolicyUrl: (docPrefix, language) =>
        `/docs/${docPrefix}aml_policy_${language}.pdf`,
      getPrivacyPolicyUrl: (docPrefix, language) =>
        docPrefix
          ? `/docs/${docPrefix}privacy_policy_${language}.pdf`
          : `/${language}/legal-documents/privacy-policy`,
      getLegalDocumentsUrl: (docPrefix, language) =>
        docPrefix ? '/legal-documents' : `/${language}/legal-documents`,
    },
  },
  {
    provide: UNAVAILABLE_MODAL_CONFIG,
    useValue: {
      translationKeyPrefix: 'unavailable-modal',
    },
  },
  {
    provide: I18N_CONFIG,
    useValue: {
      supportedLanguages: environment.supportedLanguages,
      defaultLanguage: environment.defaultLanguage,
      domainLanguages: environment.domainLanguages,
    },
  },
  {
    provide: ACCOUNT_VIEWER_CONFIG,
    useValue: {
      companyName: 'account.label.company-name-value',
    },
  },
  {
    provide: IS_JS_ENABLED,
    useFactory: (platformId: object) => isPlatformBrowser(platformId),
    deps: [PLATFORM_ID],
  },
  {
    provide: NAVIGATION_LAYOUT_CONFIG,
    useFactory: ({ hostname }: Location) =>
      <NavigationLayoutConfig>{
        copyrightText: environment.documentPrefixesByDomain[hostname]
          ? 'Mitsui Markets Ltd'
          : 'footer.copyright',
      },
    deps: [LOCATION],
  },
  verificationProviders,
  appRouting,
  provideLiveChat({
    enabled: true,
    token: environment.liveChatToken,
    showDefaultLauncher: true,
    showMobileLauncher: true,
  }),
  provideSocialNetworks({
    availableSocials: environment.availableSocials,
    growthTool: environment.growthTool,
  }),
  providePlatformLinks({
    personalArea: environment.mobilePersonalArea,
    metaTrader: environment.metaTraderLinks,
    googlePlayRestrictedCountries:
      environment.googlePlayRestrictedCountries ?? [],
  }),
  {
    provide: LAST_LOGIN_CONFIG,
    useValue: {
      storageKey: 'fbs:userLogin',
      enableMigration: true,
    },
  },
];
